import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  getPhysiciansOverview,
  OverviewPerson,
  PhysicianOverviewEntry,
} from "../api/physician";
import { adminCreateUser, adminUpdateUser } from "../api/users";
import { formatLocalDateTime } from "../utils/datetime";

// Админ-секция production-режима: список врачей (physician) со статистикой
// команды и разбивкой по каждой медсестре; создание/правка аккаунтов врачей.

function fullName(p: OverviewPerson): string {
  return [p.name, p.surname].filter(Boolean).join(" ") || "—";
}

export default function AdminPhysicians() {
  const [entries, setEntries] = useState<PhysicianOverviewEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PhysicianOverviewEntry | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      const data = await getPhysiciansOverview();
      setEntries(data.physicians);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load physicians");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper sx={{ mb: 3 }}>
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Physicians ({entries.length})
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New Physician
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={26} />
        </Box>
      ) : entries.length === 0 ? (
        <Typography sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
          No physicians yet
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Patients</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Tests</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last test</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Nurses</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((p) => (
              <Fragment key={p.id}>
                <TableRow hover>
                  <TableCell sx={{ width: 36 }}>
                    {p.members.length > 0 && (
                      <IconButton
                        size="small"
                        onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                      >
                        {expanded[p.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{fullName(p)}</TableCell>
                  <TableCell>{p.email}</TableCell>
                  <TableCell>
                    {p.is_active ? (
                      <Chip label="Active" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Disabled" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">{p.patients_total}</TableCell>
                  <TableCell align="right">{p.scans_total}</TableCell>
                  <TableCell>{p.last_scan_at ? formatLocalDateTime(p.last_scan_at) : "—"}</TableCell>
                  <TableCell align="right">{p.members.length}</TableCell>
                  <TableCell align="right" sx={{ width: 48 }}>
                    <IconButton size="small" onClick={() => setEditTarget(p)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {p.members.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                      <Collapse in={!!expanded[p.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ pl: 6, pr: 2, py: 1, bgcolor: "#fafafa", borderRadius: 1, mb: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Nurse</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">
                                  Patients created
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">
                                  Tests conducted
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {p.members.map((m) => (
                                <TableRow key={m.id}>
                                  <TableCell>{fullName(m)}</TableCell>
                                  <TableCell>{m.email}</TableCell>
                                  <TableCell>
                                    {m.is_active ? (
                                      <Chip label="Active" size="small" color="success" variant="outlined" />
                                    ) : (
                                      <Chip label="Disabled" size="small" variant="outlined" />
                                    )}
                                  </TableCell>
                                  <TableCell align="right">{m.patients_created}</TableCell>
                                  <TableCell align="right">{m.scans_conducted}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      <CreatePhysicianDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => { setCreateOpen(false); await load(); }}
      />
      <EditPhysicianDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={async () => { setEditTarget(null); await load(); }}
      />
    </Paper>
  );
}

function CreatePhysicianDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    setError("");
    setSaving(true);
    try {
      await adminCreateUser({
        email: email.trim(),
        password,
        role: "physician",
        name: name.trim(),
        surname: surname.trim(),
        is_active: true,
        is_verified: true,
      });
      setName(""); setSurname(""); setEmail(""); setPassword("");
      await onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create physician");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>New Physician</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <TextField fullWidth margin="dense" label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <TextField fullWidth margin="dense" label="Surname" value={surname} onChange={(e) => setSurname(e.target.value)} required />
        <TextField fullWidth margin="dense" label="Email (login)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <TextField fullWidth margin="dense" label="Password (min. 6 chars)" type="text" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving || !name.trim() || !surname.trim() || !email.trim() || password.length < 6}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditPhysicianDialog({
  target,
  onClose,
  onSaved,
}: {
  target: PhysicianOverviewEntry | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (target) {
      setName(target.name || "");
      setSurname(target.surname || "");
      setPassword("");
      setIsActive(target.is_active);
      setError("");
    }
  }, [target]);

  const handleSave = async () => {
    if (!target) return;
    setError("");
    setSaving(true);
    try {
      const body: { name?: string; surname?: string; password?: string; is_active?: boolean } = {};
      if (name.trim() && name.trim() !== (target.name || "")) body.name = name.trim();
      if (surname.trim() && surname.trim() !== (target.surname || "")) body.surname = surname.trim();
      if (password) body.password = password;
      if (isActive !== target.is_active) body.is_active = isActive;
      if (Object.keys(body).length > 0) {
        await adminUpdateUser(target.id, body);
      }
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Edit Physician</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {target?.email}
        </Typography>
        <TextField fullWidth margin="dense" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField fullWidth margin="dense" label="Surname" value={surname} onChange={(e) => setSurname(e.target.value)} />
        <TextField
          fullWidth margin="dense" label="New password (blank = unchanged)"
          type="text" value={password} onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          size="small"
          color={isActive ? "error" : "success"}
          onClick={() => setIsActive(!isActive)}
          sx={{ mt: 1 }}
        >
          {isActive ? "Disable account" : "Enable account"}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (password.length > 0 && password.length < 6)}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
